// Session helpers: parse GET /auth/me, build the Microsoft sign-in link safely, explain sign-in refusals.
import { paths } from './endpoints'

export interface Me {
  userId: string | null
  principalId: string | null
  email: string
  name: string
  roles: string[]
  jobTitle: string | null
  isGuest: boolean
  csrfToken: string | null
  companyName: string | null
}

function s(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null
}

/** Accepts { user: {...}, csrfToken } or a flat object. Unknown extra fields are ignored. */
export function parseMe(raw: unknown): Me {
  const r = (raw ?? {}) as Record<string, any>
  const body = r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : r
  const u = (body.user && typeof body.user === 'object' ? body.user : body) as Record<string, any>
  const p = (body.principal && typeof body.principal === 'object' ? body.principal : {}) as Record<
    string,
    any
  >
  const rolesRaw = Array.isArray(u.roles)
    ? u.roles
    : Array.isArray(body.roles)
      ? body.roles
      : Array.isArray(p.roles)
        ? p.roles
        : []
  const email = s(u.email) ?? s(body.email) ?? ''
  return {
    userId: s(u.id) ?? s(body.userId) ?? s(p.userId),
    principalId: s(p.id) ?? s(body.principalId),
    email,
    name: s(u.displayName) ?? s(u.name) ?? s(body.name) ?? s(p.name) ?? email,
    roles: rolesRaw.filter((x: unknown): x is string => typeof x === 'string'),
    jobTitle: s(u.jobTitle),
    isGuest: !!(u.isGuest ?? body.isGuest) || body.principalType === 'AUDITOR' || p.type === 'AUDITOR',
    csrfToken: s(body.csrfToken) ?? s(r.csrfToken) ?? s(body.csrf),
    companyName: s(body.company?.name) ?? s(body.companyName),
  }
}

/**
 * Only same-origin relative paths are allowed as a post-login destination (prevents open redirects).
 * Login and auth endpoints are never a destination, to avoid loops.
 */
export function safeReturnTo(path: string | null | undefined): string {
  if (!path || typeof path !== 'string') return '/'
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return '/'
  for (let i = 0; i < path.length; i++) if (path.charCodeAt(i) < 32 || path.charCodeAt(i) === 127) return '/'
  if (
    path === '/login' ||
    path.startsWith('/login?') ||
    path.startsWith('/login/') ||
    path.startsWith('/auth/')
  )
    return '/'
  return path
}

/** The "Sign in with Microsoft" link: the API starts the Entra OIDC flow and returns the user to returnTo. */
export function loginHref(returnTo?: string | null): string {
  return `${paths.login}?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`
}

/** The page the browser shows when the session is missing or expired. */
export function loginPageHref(returnTo?: string | null): string {
  const target = safeReturnTo(returnTo)
  return target === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(target)}`
}

// Codes match the API's sign-in refusals (apps/api/src/auth/errors.ts) plus standard OIDC error codes.
const LOGIN_ERRORS: Record<string, string> = {
  not_configured:
    'Microsoft sign-in is not configured on this server yet. A Clearpol administrator needs to finish the Entra app setup.',
  idp_unavailable: 'We could not reach Microsoft sign-in. Try again in a minute.',
  login_expired: 'Your sign-in attempt expired or was started in another browser tab. Sign in again.',
  access_denied: 'Microsoft sign-in was cancelled before it finished.',
  entra_error: 'Microsoft did not complete the sign-in. Try again.',
  client_secret_invalid:
    'Microsoft sign-in is misconfigured on this server. A Clearpol administrator needs to renew the app credentials.',
  sign_in_failed:
    'We could not verify the response from Microsoft. Sign in again, and contact a Clearpol administrator if it keeps happening.',
  wrong_tenant:
    'This Microsoft account belongs to a different organization. Sign in with your Clearpol account, or ask Clearpol to invite you as a guest.',
  no_email:
    'Your Microsoft account has no email address, so it cannot be matched to a Clearpol Compliance user. Contact a Clearpol administrator.',
  guest_without_role:
    'Your guest account has no role in Clearpol Compliance. Ask your Clearpol contact to assign you the Auditor role, then sign in again.',
  user_disabled:
    'Your Clearpol Compliance account is disabled. Ask a Clearpol administrator to re-enable it.',
  account_conflict:
    'Your Microsoft account conflicts with an existing Clearpol Compliance user. Contact a Clearpol administrator.',
  server_error:
    'Something went wrong on our side while signing you in. Try again in a minute, and contact a Clearpol administrator if it keeps happening.',
  consent_required: 'Your Microsoft administrator has not approved Clearpol Compliance yet.',
  interaction_required: 'Microsoft needs you to sign in again.',
  login_required: 'Your Microsoft session ended. Sign in again.',
  session_expired: 'Your session expired. Sign in again to continue.',
  signed_out: 'You have been signed out.',
}

/**
 * Turns ?error= (and optional ?error_description= / ?message=) into a sentence for the login page.
 * Known codes get a friendly message; anything else is shown as plain text (React escapes it).
 */
export function describeLoginError(
  code: string | null | undefined,
  description?: string | null,
): string | null {
  const c = typeof code === 'string' ? code.trim() : ''
  const d = typeof description === 'string' ? description.trim() : ''
  if (!c && !d) return null
  const known = LOGIN_ERRORS[c.toLowerCase()]
  if (known) return known
  const text = (d || c).slice(0, 300)
  if (/^[a-z0-9_]+$/i.test(text))
    return `Sign-in was refused (${text}). Try again or contact the Clearpol admin.`
  return text
}

/** POST /auth/logout returns the Entra end-session URL; fall back to the login page. */
export function parseLogoutUrl(raw: unknown): string {
  const r = (raw ?? {}) as Record<string, unknown>
  const body = (r.data && typeof r.data === 'object' ? r.data : r) as Record<string, unknown>
  const url = body.logoutUrl ?? body.url ?? body.redirect ?? body.redirectUrl ?? body.endSessionUrl
  if (
    typeof url === 'string' &&
    (url.startsWith('https://') || url.startsWith('http://') || url.startsWith('/'))
  ) {
    return url
  }
  return '/login?error=signed_out'
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Admin',
  INFOSEC_LEAD: 'InfoSec Lead',
  CONTROL_MANAGER: 'Control Manager',
  POLICY_MANAGER: 'Policy Manager',
  PEOPLE_OPS: 'People Ops',
  RISK_MANAGER: 'Risk Manager',
  TRUST_CENTER_MANAGER: 'Trust Center Manager',
  ACCESS_REVIEWER: 'Access Reviewer',
  AUDITOR: 'Auditor',
  EMPLOYEE: 'Employee',
  READ_ONLY: 'Read Only',
}

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role.charAt(0) + role.slice(1).toLowerCase().replace(/_/g, ' ')
}

export function initials(name: string): string {
  const parts = name
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}
